// BUILD-046 task 8.13: the Work groups page - a dedicated HQ page at /work-groups (Decision D-N, RECORDED, item 9; Req 6.1 - 6.3, 6.5, 6.17,
// 6.18, 14.20). It lists the Work_Groups, creates one, and adds or removes members, using the group routes only.
//
// It shows NO Work_Item content: administering membership and reading task content are separate authorities. Its logic and every state
// live in workGroupsModel.ts (tested, including a scan that this page names no task route). A Caller who is not a Work_Group_Administrator
// sees the not-authorized state (a failure state, never an empty list); the server checks the authority on every request regardless.
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '../../context/AuthContext';
import { setTokenProvider } from '../../api/client';
import { onCallerChanged, workApi } from '../../api/workShared';
import { createWorkGroupsController, describeWorkGroups, MAX_GROUP_NAME } from './workGroupsModel';
import type { WorkGroupsController } from './workGroupsModel';
import { ListErrorBlock, ListStateView } from './ListStateView';
import { SimulatedIdentityBanner } from './SimulatedIdentityBanner';
import { LOCAL_PILOT } from './localPilot';
// D1 display cleanup: group names are shown without the "(FICTIONAL)" test marker; members are First name + last initial (full name on hover and focus, in the Remove
// button's name), told apart by department in the "Add members" list.
import { disambiguatePeople, fullName, stripTestMarker } from './displayNames';
import { PersonName } from './PersonName';
// Increment 1b-i: a small decorative initials circle before each person in the Add members list.
import { AvatarCircle } from './PersonAvatar';
import { avatarLabels } from './personAvatarModel';

export function WorkGroupsPage() {
  const { getToken } = useAuth();
  const ref = useRef<WorkGroupsController | null>(null);
  if (ref.current === null) ref.current = createWorkGroupsController(workApi, () => workApi.getCallerEpoch());
  const controller = ref.current;
  const state = useSyncExternalStore(controller.subscribe, controller.getState);

  useEffect(() => {
    setTokenProvider(getToken);
    void controller.start();
    return onCallerChanged(() => {
      void controller.callerChanged();
    });
  }, [controller]);

  const view = describeWorkGroups(state);

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6" data-testid="work-groups-page">
      {/* The persona control used to be in the page header (it overlapped the navigation); in the local pilot it is in this notice (correction cycle 1bb88b3e, N9). */}
      <SimulatedIdentityBanner me={null} localPilot={LOCAL_PILOT} />
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Work groups</h1>
      <p className="text-sm text-gray-500 mb-4">Create groups and manage who belongs to them. Groups are used to send a task to a team. This page shows no tasks.</p>

      {view.body === 'checking' && (
        <div className="py-8 text-center text-gray-500 text-sm" role="status" aria-live="polite">
          Checking your access…
        </div>
      )}

      {view.body === 'denied' && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm" data-testid="work-groups-denied">
          {view.deniedText}
        </div>
      )}

      {view.body === 'error' && <ListErrorBlock heading="Could not check your access" detail={view.accessError ?? ''} onRetry={() => void controller.start()} listLabel="your access" />}

      {view.body === 'ready' && (
        <>
          {view.banner !== null && (
            <div
              role={view.banner.kind === 'error' ? 'alert' : 'status'}
              aria-live={view.banner.kind === 'error' ? 'assertive' : 'polite'}
              className={`mb-3 px-3 py-2 rounded text-sm flex items-start justify-between gap-2 ${view.banner.kind === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-800'}`}
            >
              <span>{view.banner.text}</span>
              <button type="button" onClick={() => controller.dismissBanner()} aria-label="Dismiss message" className="font-bold">
                &times;
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ── groups ── */}
            <section aria-labelledby="work-groups-heading" className="space-y-3">
              <h2 id="work-groups-heading" className="text-sm font-semibold text-gray-700">
                Groups
              </h2>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void controller.createGroup();
                }}
                className="bg-white rounded-lg border border-gray-200 p-3 space-y-2"
              >
                <label htmlFor="work-group-name" className="block text-xs font-medium text-gray-700">
                  New group name
                </label>
                <input
                  id="work-group-name"
                  type="text"
                  value={state.name}
                  onChange={(event) => controller.setName(event.target.value)}
                  aria-describedby="work-group-name-hint"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder={`Up to ${MAX_GROUP_NAME} characters`}
                />
                <p id="work-group-name-hint" className="text-xs text-amber-700 min-h-[1rem]" role="status" aria-live="polite">
                  {view.hint ?? ''}
                </p>
                <button type="submit" disabled={!view.canCreate} className="px-3 py-1.5 bg-[#4AABDE] text-white text-xs font-medium rounded disabled:opacity-50">
                  {view.saving ? 'Saving…' : 'Create group'}
                </button>
              </form>

              <ListStateView
                view={view.groups}
                listLabel="work groups"
                onRetry={() => void controller.retryGroups()}
                renderItems={(groups) => (
                  <ul className="space-y-1" aria-label="Work groups">
                    {groups.map((group) => {
                      const selected = group.groupId === state.selectedGroupId;
                      return (
                        <li key={group.groupId}>
                          <button
                            type="button"
                            onClick={() => void controller.selectGroup(group.groupId)}
                            aria-pressed={selected}
                            className={`w-full text-left px-3 py-2 rounded border text-sm ${selected ? 'bg-white border-[#4AABDE] font-medium text-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}
                          >
                            {stripTestMarker(group.name)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              />
            </section>

            {/* ── members of the selected group ── */}
            <section aria-labelledby="work-group-members-heading" className="space-y-3">
              <h2 id="work-group-members-heading" className="text-sm font-semibold text-gray-700">
                {view.selectedGroup !== null ? `Members of ${stripTestMarker(view.selectedGroup.name)}` : 'Members'}
              </h2>
              {view.members === null ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 text-sm">Choose a group to see and change its members.</div>
              ) : (
                <>
                  <ListStateView
                    view={view.members}
                    listLabel="members"
                    onRetry={() => void controller.retryMembers()}
                    renderItems={(members) => (
                      <ul className="space-y-1" aria-label="Group members">
                        {members.map((member) => (
                          <li key={member.employeeId} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span className="text-gray-900">
                              {/* The row's Remove button is the tab stop and carries the full name in its name and title, so the name itself is not a second stop. */}
                              <PersonName person={member.displayName} focusable={false} />
                              {member.status === 'inactive' && <span className="ml-2 text-xs text-gray-500">(inactive)</span>}
                            </span>
                            <button
                              type="button"
                              disabled={view.saving}
                              onClick={() => void controller.removeMember(member.employeeId)}
                              aria-label={`Remove ${fullName(member.displayName)} from ${view.selectedGroup !== null ? stripTestMarker(view.selectedGroup.name) : 'the group'}`}
                              title={`Remove ${fullName(member.displayName)}`}
                              className="text-xs text-red-600 hover:bg-red-50 rounded px-2 py-0.5 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  />

                  {/* ── add members ── */}
                  <fieldset className="bg-white rounded-lg border border-gray-200 p-3">
                    <legend className="px-1 text-xs font-medium text-gray-700">Add members</legend>
                    <ListStateView
                      view={view.employees}
                      listLabel="people"
                      onRetry={() => void controller.retryEmployees()}
                      renderItems={() =>
                        view.candidates.length === 0 ? (
                          <p className="text-xs text-gray-500 py-2">Everyone listed is already a member.</p>
                        ) : (
                          <ul className="max-h-48 overflow-y-auto space-y-1">
                            {(() => {
                              const choices = disambiguatePeople(view.candidates);
                              // The circles are labelled over the whole candidate list, so no two in it look alike; the colour is the person's (the employee id).
                              const circles = avatarLabels(view.candidates.map((person, index) => ({ id: person.employeeId, name: person.displayName, department: choices[index].department })));
                              return view.candidates.map((person, index) => (
                                <li key={person.employeeId}>
                                  <label className="flex items-center gap-2 text-sm text-gray-800" title={choices[index].accessibleName}>
                                    <input type="checkbox" checked={state.toAdd.includes(person.employeeId)} onChange={() => controller.toggleToAdd(person.employeeId)} aria-label={choices[index].accessibleName} />
                                    <AvatarCircle badge={circles[index]} size="compact" />
                                    <span>
                                      {choices[index].short}
                                      {choices[index].detail !== '' && <span className="ml-2 text-xs text-gray-500">{choices[index].detail}</span>}
                                    </span>
                                  </label>
                                </li>
                              ));
                            })()}
                          </ul>
                        )
                      }
                    />
                    <button type="button" disabled={!view.canAdd} onClick={() => void controller.addSelected()} className="mt-2 px-3 py-1.5 bg-[#4AABDE] text-white text-xs font-medium rounded disabled:opacity-50">
                      {view.saving ? 'Saving…' : 'Add selected'}
                    </button>
                  </fieldset>
                </>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
